import { connect } from 'react-redux';
import { push } from 'redux-first-history';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import RenterDashboardV2 from './component';
import { pageFlows as paymentSettingsFlows } from '@renter-hub/pages/PaymentSettingsPage/pageFlows';
import type { ErrorInterface } from '@common/types';
import { getRachelDashboard, requestUpdateModeLinkToken } from '@renter-hub/actions/requests';
import {
  closeNotification,
  showPageNotification,
  showStackedPageNotification,
} from '@common/actions/NotificationActions';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import { MOVE_IN_COSTS_PAYMENT, RENT_PAYMENT } from '@renter-hub/constants/paymentConstants';
import {
  DEFAULT_ERROR,
  PAST_DUE_WARNING_IF_PAYMENT_PLAN_AVAILABLE,
  PAYMENT_UPDATED_SUCCESS,
  PLAID_CREATE_PAYMENT_METHOD_ERROR,
  RENT_AMOUNT_CHANGE_EXPIRED,
  RENT_AMOUNT_CHANGE_REMINDER,
  VERIFY_MULTIPLE_BANK_ACCOUNTS_BANNER,
  VERIFY_SINGLE_BANK_ACCOUNT_BANNER,
} from '@renter-hub/constants/notifications';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import paths from '@renter-hub/pathsManifest';
import { logError } from '@zg-rentals/log-error';
import { trackValidationError } from '@common/actions/TrackingActions';

export const mapStateToProps = (state) => {
  // const { featurePermissions } = state.payments.renter.dashboard;
  // const latePaymentsAllowed = featurePermissions?.[FeaturePermissionTypes.LATE_PAYMENTS]?.isAllowed;
  return {
    ...state.payments.renter.dashboard,
    latePaymentsAllowed: true,
    user: state.user,
    config: state.payments.renter.config,
    fromEmail: state.router.location.search ? state.router.location.search.indexOf('email') !== -1 : false,
    notification: state.payments.renter.notification,
    featureFlagEnableRentReporting: state.payments.renter.dashboard.enableCreditReporting,
    paymentMethods: state.payments.renter.dashboard.paymentMethods,
    enablePaymentStatus: state.payments.renter.config.enablePaymentStatus,
    enableCreditTracking: state.payments.renter.dashboard.enableCreditTracking,
    location: state.router.location,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId }) => ({
  displayRentAmountChangeReminder: ({ newAmount, effectiveDueDate }) => {
    dispatch(
      showStackedPageNotification(RENT_AMOUNT_CHANGE_REMINDER, {
        newAmountFormatted: centsToDollars(newAmount),
        effectiveDueDateFormatted: formatDate(effectiveDueDate, 'MMMM do'),
      }),
    );
  },
  displayExpiredRentAmountChange: ({ landlordEmail, landlordFirstName }) => {
    dispatch(
      showStackedPageNotification(RENT_AMOUNT_CHANGE_EXPIRED, {
        landlordEmail,
        landlordFirstName,
      }),
    );
  },
  displayPastDueWarningWhenPaymentPlanRequested: () => {
    dispatch(showStackedPageNotification(PAST_DUE_WARNING_IF_PAYMENT_PLAN_AVAILABLE));
  },
  displayVerifySingleBankAccountBanner: ({
    paymenMethodStatus,
    accountLast4,
    plaidEnv,
    plaidLinkToken,
    plaidLinkTokenExpiration,
    getPlaidLinkToken,
    onSuccess,
    onExit,
  }) => {
    dispatch(
      showStackedPageNotification(VERIFY_SINGLE_BANK_ACCOUNT_BANNER, {
        paymenMethodStatus,
        accountLast4,
        plaidEnv,
        plaidLinkToken,
        plaidLinkTokenExpiration,
        getPlaidLinkToken,
        onSuccess,
        onExit,
      }),
    );
  },
  displayVerifyMultipleBankAccountsBanner: ({ onManagePaymentMethodsClick }) => {
    dispatch(
      showStackedPageNotification(VERIFY_MULTIPLE_BANK_ACCOUNTS_BANNER, {
        onManagePaymentMethodsClick: () => {
          dispatch(closeNotification());
          onManagePaymentMethodsClick();
        },
      }),
    );
  },
  payOneTimePaymentOnClick: async (otpKey: string) => {
    await dispatch(closeNotification());
    await dispatch(push(`${baseUrl}${paths.oneTimePaymentPage}?otpKey=${otpKey}`));
  },
  payRentPaymentOnClick: async (scheduleMonth: string) => {
    await dispatch(closeNotification());
    await dispatch(push(`${baseUrl}${paths.payPastDueRentPaymentPage}?scheduleMonth=${scheduleMonth}`));
  },
  goToEditPaymentInfoPage: async (typeOfPaymentToMake) => {
    // Ensure typeOfPaymentToMake is a string and matches expected values
    const isValidPaymentType =
      typeof typeOfPaymentToMake === 'string' &&
      (typeOfPaymentToMake === RENT_PAYMENT || typeOfPaymentToMake === MOVE_IN_COSTS_PAYMENT);

    const state = isValidPaymentType ? { typeOfPaymentToMake } : undefined;

    await dispatch(closeNotification());
    await dispatch(push({ pathname: baseUrl + paths.updatePaymentMethodsPage, state }));
  },
  goToManagePaymentMethodsPage: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.managePaymentMethodsPage));
  },
  goToEditPaymentFrequencyPage: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.editPaymentSettingsPage));
  },
  goToEditPaymentSchedulingPage: async (recurring) => {
    await dispatch(closeNotification());
    await dispatch(
      push({
        pathname: baseUrl + paths.editPaymentSettingsPage,
        state: {
          startPage: recurring
            ? paymentSettingsFlows.RecurringSchedulingPage
            : paymentSettingsFlows.ManualSchedulingPage,
        },
      }),
    );
  },
  goToCancelPaymentPage: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.cancelPaymentPage));
  },
  goToEditMoveInCostsPage: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.editMoveInCostsSchedulePage));
  },
  goToCancelMoveInCostsPage: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.cancelMoveInCostsPage));
  },
  goToPaymentReportingPage: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.editPaymentReportingEnrollmentPage));
  },
  goToRentReportingPage: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.rentReportingPage));
  },
  paymentReportingSuccessModalClick: async () => {
    await dispatch(closeNotification());
  },
  getPlaidUpdateModeLinkToken: async (externalId) => {
    const response = await dispatch(requestUpdateModeLinkToken(externalId));
    if (response.payload?.error) {
      return { error: true };
    }
    return response;
  },
  onPaymentMethodCreationSuccess: async () => {
    try {
      dispatch(closeNotification());
      // Request the latest renter status
      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      // Show the payment updated success banner
      await dispatch(showPageNotification(PAYMENT_UPDATED_SUCCESS));

      return { error: false };
    } catch (err) {
      logError({ error: err });
      dispatch(showPageNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  onPaymentMethodCreationError: async (error: unknown) => {
    const err = error as ErrorInterface;
    const message = err instanceof Error ? err.message : undefined;

    logError({ error });

    if (message) {
      await dispatch(showPageNotification(PLAID_CREATE_PAYMENT_METHOD_ERROR, { message }));
    } else {
      await dispatch(showPageNotification(DEFAULT_ERROR));
    }
    return { error: true };
  },

  goToDashboard: async () => {
    await dispatch(getRachelDashboard({ propertyId }));
  },
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RenterDashboardV2);
